// Draws the region scope (fields, hubs, filed plans, drones, wind) on a canvas.

import {
  FIELDS, HUBS, CONFLICT_DIST, PREDICT_URGENT, POWER,
} from '../config/constants.js';
import { opsType, OPS_LOOKAHEAD } from '../core/ops.js';
import { ZONES, zonesSummary } from '../core/zones.js';
import { COLORS, MONO } from '../config/theme.js';
import { windAt } from '../core/sim.js';
import { drawGrid as artGrid, drawField, drawHub, drawBlip, drawCallsign, drawWarningRing as artRing } from '../shared/art.js';

export function createRenderer(canvas, container) {
  const ctx = canvas.getContext('2d');
  let w = 0;
  let h = 0;

  // The canvas is absolutely positioned over the container (see main.css), so
  // sizing it never changes the container's size -- otherwise a
  // ResizeObserver on the container would feed back into itself and grow
  // the scope forever.
  function resize() {
    const dpr = window.devicePixelRatio || 1;
    w = container.clientWidth;
    h = container.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(container);
  else window.addEventListener('resize', resize);
  resize();

  const px = (p) => ({ x: p.x * w, y: p.y * h });
  const minDim = () => Math.min(w, h);

  // Grid, fields and hubs use the drawing primitives shared with the phone
  // version (src/shared/art.js), so both scopes look alike.
  function drawGrid() {
    artGrid(ctx, { w, h });
  }

  function drawFields() {
    for (const f of FIELDS) {
      const p = px(f);
      drawField(ctx, { x: p.x, y: p.y, w: f.w * w, h: f.h * h, label: f.name });
    }
  }

  function drawHubs() {
    for (const hub of HUBS) {
      const p = px(hub);
      drawHub(ctx, { x: p.x, y: p.y, r: minDim() * 0.028, label: hub.name });
    }
  }

  // ---- field and hub alerts (#31) -------------------------------------------

  // Power lines scouts found, and towers (dashed until the field is re-scouted).
  function drawFieldHazards(ops) {
    for (const f of ops.fields) {
      if (f.wire) {
        const a = px(f.wire.from);
        const b = px(f.wire.to);
        ctx.save();
        ctx.strokeStyle = COLORS.wheat;
        ctx.lineWidth = 1.4;
        ctx.setLineDash([1, 3]);
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.font = `600 8.5px ${MONO}`;
        ctx.fillStyle = COLORS.wheat;
        ctx.fillText('POWER LINE', Math.min(a.x, b.x) + 6, (a.y + b.y) / 2 - 5);
        ctx.restore();
      }
      if (f.tower) drawTower(px(f.tower), f.tower.marked);
    }
  }

  function drawTower(c, marked) {
    ctx.save();
    ctx.strokeStyle = marked ? COLORS.wheat : COLORS.alertSolid;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(c.x - 6, c.y + 8); ctx.lineTo(c.x, c.y - 9); ctx.lineTo(c.x + 6, c.y + 8);
    ctx.moveTo(c.x - 3.5, c.y + 1); ctx.lineTo(c.x + 3.5, c.y + 1);
    ctx.stroke();
    ctx.font = `600 8.5px ${MONO}`;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.fillText(marked ? 'TOWER' : 'NEW TOWER: RE-SCOUT', c.x + 9, c.y + 3);
    ctx.restore();
  }

  // Hub badges: a blocked pad gets a red X, low power a lightning bolt.
  function drawHubAlerts(alerts) {
    for (const a of alerts.list) {
      if (a.kind !== 'padBlocked' && a.kind !== 'lowPower') continue;
      const c = px(HUBS[a.hubIdx]);
      const r = minDim() * 0.028;
      ctx.save();
      ctx.font = `700 9px ${MONO}`;
      ctx.textAlign = 'center';
      if (a.kind === 'padBlocked') {
        ctx.strokeStyle = COLORS.alertSolid;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(c.x - r * 0.7, c.y - r * 0.7); ctx.lineTo(c.x + r * 0.7, c.y + r * 0.7);
        ctx.moveTo(c.x + r * 0.7, c.y - r * 0.7); ctx.lineTo(c.x - r * 0.7, c.y + r * 0.7);
        ctx.stroke();
        ctx.fillStyle = COLORS.alertSolid;
        ctx.fillText('PAD BLOCKED', c.x, c.y - r - 6);
      } else {
        ctx.fillStyle = COLORS.wheat;
        ctx.fillText('⚡ LOW POWER', c.x, c.y - r - (alerts.list.some((x) => x.kind === 'padBlocked' && x.hubIdx === a.hubIdx) ? 17 : 6));
      }
      ctx.restore();
    }
  }

  // People walking across fields, and bird flocks drifting over the farm.
  function drawFieldAlerts(alerts, elapsed) {
    for (const a of alerts.list) {
      if (a.kind === 'person') {
        const c = px(a);
        ctx.save();
        // the 100 ft exclusion zone, roughly
        ctx.beginPath();
        ctx.arc(c.x, c.y, minDim() * 0.035, 0, Math.PI * 2);
        ctx.strokeStyle = COLORS.wheat;
        ctx.setLineDash([2, 3]);
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = COLORS.ink;
        ctx.beginPath(); ctx.arc(c.x, c.y - 6, 2.6, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = COLORS.ink;
        ctx.lineWidth = 1.6;
        const step = Math.sin(elapsed * 8) * 2.5;
        ctx.beginPath();
        ctx.moveTo(c.x, c.y - 3); ctx.lineTo(c.x, c.y + 3);
        ctx.moveTo(c.x, c.y + 3); ctx.lineTo(c.x - 2.5 + step, c.y + 8);
        ctx.moveTo(c.x, c.y + 3); ctx.lineTo(c.x + 2.5 - step, c.y + 8);
        ctx.moveTo(c.x - 3, c.y); ctx.lineTo(c.x + 3, c.y);
        ctx.stroke();
        ctx.restore();
      } else if (a.kind === 'birds') {
        const c = px(a);
        const r = a.r * minDim();
        ctx.save();
        ctx.beginPath();
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(209,97,74,0.08)';
        ctx.fill();
        ctx.strokeStyle = COLORS.alert;
        ctx.setLineDash([3, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
        // a loose flock of little Vs, flapping
        ctx.strokeStyle = COLORS.ink;
        ctx.lineWidth = 1.2;
        for (let i = 0; i < 9; i++) {
          const ang = i * 2.4 + a.id;
          const rr = r * (0.2 + ((i * 37) % 10) / 14);
          const bx = c.x + Math.cos(ang) * rr;
          const by = c.y + Math.sin(ang) * rr * 0.7;
          const flap = 2 + Math.sin(elapsed * 10 + i) * 1.6;
          ctx.beginPath();
          ctx.moveTo(bx - 3.5, by - flap); ctx.lineTo(bx, by); ctx.lineTo(bx + 3.5, by - flap);
          ctx.stroke();
        }
        ctx.font = `600 8.5px ${MONO}`;
        ctx.fillStyle = COLORS.alertSolid;
        ctx.fillText('BIRDS', c.x - 12, c.y - r - 4);
        ctx.restore();
      }
    }
  }

  // ---- filed plans -------------------------------------------------------

  // How urgent a predicted conflict is: 0 when it's far off (or there's none),
  // rising to 1 at launch. Grounded plans aren't launching, so they're never urgent.
  function urgencyOf(p, elapsed) {
    if (!p.predicted || p.grounded) return 0;
    const secs = p.departAt - elapsed;
    return secs >= PREDICT_URGENT ? 0 : 1 - Math.max(0, secs) / PREDICT_URGENT;
  }

  function planLabel(p, elapsed) {
    if (p.grounded) return p.windHeld ? 'WIND HOLD' : `T-${Math.ceil(p.departAt - elapsed)}s WIND`;
    const t = `T-${Math.max(0, Math.ceil(p.departAt - elapsed))}s`;
    return p.predicted ? `${t} \u26A0` : t;
  }

  function drawPlanLine(p, stateColor, urgency, selected, elapsed) {
    const a = px(p.from);
    const b = px(p.to);
    ctx.save();
    ctx.setLineDash(p.grounded ? [2, 5] : [5, 5]);
    // an urgent conflict's dashes march toward the destination
    if (urgency > 0) ctx.lineDashOffset = -elapsed * 24;
    // selected: same colour, drawn bolder (the light ghost colour means "a new route")
    ctx.strokeStyle = stateColor || (selected ? p.color : `${p.color}70`);
    ctx.lineWidth = (stateColor ? 1.6 : 1.1) + urgency * 1.4 + (selected ? 0.9 : 0);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.restore();
  }

  // ticket marker at the origin (hollow while grounded)
  function drawPlanMarker(p, stateColor) {
    const a = px(p.from);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(a.x, a.y - 5); ctx.lineTo(a.x + 5, a.y); ctx.lineTo(a.x, a.y + 5); ctx.lineTo(a.x - 5, a.y);
    ctx.closePath();
    if (p.grounded) { ctx.strokeStyle = COLORS.ground; ctx.lineWidth = 1.4; ctx.stroke(); }
    else { ctx.fillStyle = stateColor || p.color; ctx.fill(); }
    ctx.restore();
  }

  // Pulsing halo that tightens and speeds up as a conflicted launch nears.
  function drawWarningRing(at, urgency, elapsed) {
    const c = px(at);
    const pulse = 0.5 + 0.5 * Math.sin(elapsed * (5 + urgency * 9));
    ctx.save();
    ctx.beginPath();
    ctx.arc(c.x, c.y, 9 + pulse * 3 + (1 - urgency) * 4, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.predictGlow;
    ctx.fill();
    ctx.strokeStyle = COLORS.predict;
    ctx.lineWidth = 1.2 + urgency;
    ctx.stroke();
    ctx.restore();
  }

  // Small cross where the predicted conflict would happen.
  function drawConflictMark(at, urgency) {
    const c = px(at);
    const r = 4 + urgency * 2;
    ctx.save();
    ctx.strokeStyle = COLORS.predict;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(c.x - r, c.y - r); ctx.lineTo(c.x + r, c.y + r);
    ctx.moveTo(c.x + r, c.y - r); ctx.lineTo(c.x - r, c.y + r);
    ctx.stroke();
    ctx.restore();
  }

  function drawPlanLabel(p, elapsed, urgency, stackIndex) {
    const a = px(p.from);
    ctx.save();
    ctx.font = `${urgency > 0 ? 600 : 500} 9.5px ${MONO}`;
    ctx.fillStyle = urgency > 0 ? COLORS.predictInk : COLORS.label;
    ctx.textBaseline = 'bottom';
    ctx.fillText(`${p.call} ${planLabel(p, elapsed)}`, a.x + 8, a.y - 6 - stackIndex * 11);
    ctx.restore();
  }

  function drawSelectionRing(at, radius) {
    const c = px(at);
    ctx.save();
    ctx.beginPath();
    ctx.arc(c.x, c.y, radius, 0, Math.PI * 2);
    ctx.strokeStyle = COLORS.selection;
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.restore();
  }

  function drawFiledPlan(p, elapsed, selected, stackIndex) {
    // a wind hold outranks a predicted conflict -- it can't fly either way
    const stateColor = p.grounded ? COLORS.ground : (p.predicted ? COLORS.predict : null);
    const urgency = urgencyOf(p, elapsed);
    drawPlanLine(p, stateColor, urgency, selected, elapsed);
    if (p.predicted && !p.grounded && p.conflictAt) drawConflictMark(p.conflictAt, urgency);
    if (urgency > 0) drawWarningRing(p.from, urgency, elapsed);
    drawPlanMarker(p, stateColor);
    if (selected) drawSelectionRing(p.from, 9);
    drawPlanLabel(p, elapsed, urgency, stackIndex);
  }

  function drawDrone(d, elapsed, selected) {
    const p = px(d);
    if (d.state === 'landed') {
      // on a pad for its turnaround: small and quiet
      ctx.save();
      ctx.globalAlpha = 0.6;
      drawBlip(ctx, { x: p.x, y: p.y, color: d.color });
      ctx.restore();
      if (selected) drawSelectionRing(d, 8);
      return;
    }
    if (d.trail.length > 1) {
      ctx.save();
      ctx.beginPath();
      d.trail.forEach((t, i) => { const tp = px(t); if (i === 0) ctx.moveTo(tp.x, tp.y); else ctx.lineTo(tp.x, tp.y); });
      ctx.strokeStyle = `${d.color}55`;
      ctx.lineWidth = 1.3;
      ctx.stroke();
      ctx.restore();
    }
    if (d.state === 'conflict') {
      const pulse = 5 + Math.sin(elapsed * 8 + d.id) * 2;
      artRing(ctx, { x: p.x, y: p.y, r: minDim() * CONFLICT_DIST * 0.5 + pulse });
    }
    if (d.state === 'holding') {
      ctx.save();
      const hr = minDim() * 0.0396;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, hr, hr * 0.6, 0, 0, Math.PI * 2);
      ctx.strokeStyle = COLORS.holdRing;
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(p.x + Math.cos(d.holdAngle) * hr, p.y + Math.sin(d.holdAngle) * hr * 0.6, 2.6, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.wheat;
      ctx.fill();
      ctx.restore();
    }
    if (selected) drawSelectionRing(d, 8);
    drawBlip(ctx, { x: p.x, y: p.y, color: d.color });
    drawCallsign(ctx, { x: p.x, y: p.y, text: d.call });
    if (d.emergency) drawEmergency(p, d.emergency.left, elapsed);
  }

  // Battery or fuel left (#32): a small bar under the aircraft.
  function drawEnergyBar(c, a) {
    const wBar = 16;
    const x = c.x - wBar / 2;
    const y = c.y + 11;
    ctx.save();
    ctx.fillStyle = 'rgba(10,15,18,0.7)';
    ctx.fillRect(x - 1, y - 1, wBar + 2, 4);
    ctx.fillStyle = a.energy > 0.5 ? COLORS.crop : (a.energy > 0.25 ? COLORS.wheat : COLORS.alertSolid);
    ctx.fillRect(x, y, wBar * a.energy, 2);
    ctx.restore();
  }

  // A craft that must land now: a fast red pulse and its countdown (#33).
  function drawEmergency(p, left, elapsed) {
    const pulse = 9 + Math.abs(Math.sin(elapsed * 6)) * 5;
    ctx.save();
    ctx.beginPath();
    ctx.arc(p.x, p.y, pulse, 0, Math.PI * 2);
    ctx.strokeStyle = COLORS.alertSolid;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.font = `700 9px ${MONO}`;
    ctx.fillStyle = COLORS.alertSolid;
    ctx.fillText(`EMERGENCY ${Math.max(0, Math.ceil(left))}s`, p.x + 10, p.y + 16);
    ctx.restore();
  }

  // Landing zones (#33): field pads, and pads and pattern counts at hubs.
  function drawZones(s) {
    for (const row of zonesSummary(s)) {
      const { zone, used, blocked, pattern, full } = row;
      const c = px(zone);
      ctx.save();
      ctx.font = `600 8.5px ${MONO}`;
      ctx.textAlign = 'center';
      const color = full ? COLORS.alertSolid : COLORS.label;
      if (zone.hubIdx === null) {
        const r = 7;
        ctx.strokeStyle = full ? COLORS.alertSolid : 'rgba(236,223,196,0.45)';
        ctx.lineWidth = 1.2;
        ctx.strokeRect(c.x - r, c.y - r, r * 2, r * 2);
        ctx.fillStyle = ctx.strokeStyle;
        ctx.fillText('P', c.x, c.y + 3);
        ctx.fillStyle = color;
        ctx.fillText(`${used}/${zone.capacity}`, c.x, c.y + r + 10);
      } else {
        const r = minDim() * 0.028;
        // what this hub refills (#32)
        ctx.fillStyle = COLORS.wheat;
        ctx.fillText(HUBS[zone.hubIdx].supplies.map((p) => `${POWER[p].icon} ${POWER[p].name.toUpperCase()}`).join(' · '), c.x, c.y + r + 25);
        ctx.fillStyle = color;
        let text = blocked ? '' : `PADS ${used}/${zone.capacity}`;
        if (pattern) text += `${text ? ' · ' : ''}PATTERN ${pattern}`;
        if (text) ctx.fillText(text, c.x, c.y + r + 36);
      }
      ctx.restore();
    }
  }

  function drawWindIndicator(s) {
    const wind = windAt(s, s.elapsed);
    const cx = w - 46;
    const cy = 46;
    const r = 26;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = COLORS.windRing;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.translate(cx, cy);
    ctx.rotate(wind.dir * Math.PI / 180 + Math.PI); // arrow points where the wind blows TO
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.75); ctx.lineTo(6, r * 0.35); ctx.lineTo(0, r * 0.15); ctx.lineTo(-6, r * 0.35);
    ctx.closePath();
    ctx.fillStyle = COLORS.predict;
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.font = `600 10px ${MONO}`;
    ctx.fillStyle = COLORS.predictInk;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${Math.round(wind.speed)}mph`, cx, cy + r + 4);
    ctx.restore();
  }

  // ---- crop-dusting operations (#13) ---------------------------------------

  const FIELD_TAG = {
    unscouted: 'TO SCOUT', scouting: 'SCOUTING', clear: 'CLEAR \u00b7 READY TO SPRAY', spraying: 'SPRAYING', sprayed: 'SPRAYED',
  };
  const FIELD_COLOR = {
    unscouted: COLORS.toScout, scouting: COLORS.predict, clear: COLORS.crop, spraying: COLORS.crop, sprayed: COLORS.crop,
  };

  // The first `progress` (0..1) of a polyline, in px.
  function partialPolyline(pts, progress) {
    const p = pts.map(px);
    let total = 0;
    for (let i = 1; i < p.length; i++) total += Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y);
    let left = total * Math.max(0, Math.min(1, progress));
    const out = [p[0]];
    for (let i = 1; i < p.length && left > 0; i++) {
      const seg = Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y);
      if (seg <= left) { out.push(p[i]); left -= seg; continue; }
      const k = left / seg;
      out.push({ x: p[i - 1].x + (p[i].x - p[i - 1].x) * k, y: p[i - 1].y + (p[i].y - p[i - 1].y) * k });
      left = 0;
    }
    return out;
  }

  function strokePolyline(pts, {
    color, width, dash = null, cap = 'round', join = 'round',
  }) {
    if (pts.length < 2) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = cap;
    ctx.lineJoin = join;
    if (dash) ctx.setLineDash(dash);
    ctx.beginPath();
    pts.forEach((pt, i) => (i ? ctx.lineTo(pt.x, pt.y) : ctx.moveTo(pt.x, pt.y)));
    ctx.stroke();
    ctx.restore();
  }

  // Work-order fields: state outline, the passes flown so far, and a tag.
  function drawWorkOrder(ops) {
    for (const f of ops.fields) {
      const r = FIELDS[f.idx];
      const p = px(r);
      const fw = r.w * w;
      const fh = r.h * h;
      const color = FIELD_COLOR[f.state];
      ctx.save();
      if (f.state === 'sprayed') { ctx.fillStyle = COLORS.sprayedFill; ctx.fillRect(p.x, p.y, fw, fh); }
      ctx.strokeStyle = color;
      ctx.lineWidth = f.state === 'unscouted' ? 1.2 : 1.6;
      if (f.state === 'unscouted' || f.state === 'scouting') ctx.setLineDash([6, 4]);
      ctx.strokeRect(p.x + 0.5, p.y + 0.5, fw - 1, fh - 1);
      ctx.restore();
      if (f.spray) {
        const lanes = opsType('duster').lanes;
        const band = Math.max(4, (fh * 0.76) / Math.max(1, lanes - 1));
        strokePolyline(partialPolyline(f.spray.pattern, f.spray.progress), { color: COLORS.sprayBand, width: band, cap: 'butt', join: 'miter' }); // square lane ends, not lumpy
      }
      if (f.sweep && f.state !== 'sprayed') {
        strokePolyline(partialPolyline(f.sweep.pattern, f.sweep.progress), { color: COLORS.sweepLine, width: 1.2 });
      }
      const pct = f.state === 'scouting' ? f.sweep.progress : (f.state === 'spraying' ? f.spray.progress : null);
      ctx.save();
      ctx.font = `600 9.5px ${MONO}`;
      ctx.fillStyle = color;
      ctx.textBaseline = 'bottom';
      ctx.fillText(FIELD_TAG[f.state] + (pct === null ? '' : ` ${Math.round(pct * 100)}%`), p.x + 8, p.y + fh - 7);
      ctx.restore();
    }
  }

  const OPS_STATUS = {
    sprayHold: 'SPRAY HOLD', peopleHold: 'PERSON IN FIELD', holding: 'HOLD', returning: 'RTB',
    padHold: 'PAD BLOCKED', recharging: 'REFILLING',
  };

  function drawOpsAircraft(ops, selection, elapsed) {
    for (const a of ops.aircraft) {
      const c = px(a);
      if (a.path && a.idx < a.path.length) {
        const rest = [c, ...a.path.slice(a.idx).map(px)];
        strokePolyline(rest, { color: `${a.color}88`, width: 1.2, dash: [4, 4] });
      }
      if (a.predict && !a.alert) {
        // where it will lose separation, and a line to it
        strokePolyline([c, px(a.predict.at)], { color: COLORS.predict, width: 1.2, dash: [2, 3] });
        drawConflictMark(a.predict.at, 1 - a.predict.in / OPS_LOOKAHEAD);
      }
      if (a.emergency) drawEmergency(c, a.emergency.left, elapsed);
      drawEnergyBar(c, a);
      if (a.alert) {
        const pulse = 5 + Math.sin(elapsed * 8) * 2;
        artRing(ctx, { x: c.x, y: c.y, r: minDim() * CONFLICT_DIST * 0.5 + pulse });
      }
      if (a.state === 'holding') {
        const hr = minDim() * 0.0396;
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, hr, hr * 0.6, 0, 0, Math.PI * 2);
        ctx.strokeStyle = COLORS.holdRing;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.restore();
      }
      if (selection && selection.kind === 'ops' && selection.id === a.id) drawSelectionRing(a, 12);
      // a thin ring marks the farmer's own aircraft apart from other traffic
      ctx.save();
      ctx.beginPath();
      ctx.arc(c.x, c.y, 8.5, 0, Math.PI * 2);
      ctx.strokeStyle = COLORS.ownRing;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
      if (a.type === 'duster') {
        drawBlip(ctx, { x: c.x, y: c.y, r: 5.2, color: a.color, stroke: COLORS.blipStroke });
        const spin = a.state === 'parked' ? 0.6 : elapsed * 14;
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(spin);
        ctx.strokeStyle = COLORS.ink;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(-9, 0); ctx.lineTo(9, 0); ctx.moveTo(0, -9); ctx.lineTo(0, 9);
        ctx.stroke();
        ctx.restore();
      } else {
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = a.color;
        ctx.strokeStyle = COLORS.blipStroke;
        ctx.fillRect(-4.2, -4.2, 8.4, 8.4);
        ctx.strokeRect(-4.2, -4.2, 8.4, 8.4);
        ctx.restore();
      }
      drawCallsign(ctx, { x: c.x, y: c.y, text: a.call, color: COLORS.ink, font: `600 10px ${MONO}`, dx: 10, dy: -6 });
      const status = OPS_STATUS[a.state];
      if (status) {
        ctx.save();
        ctx.font = `600 9px ${MONO}`;
        ctx.fillStyle = ['sprayHold', 'peopleHold', 'padHold', 'recharging'].includes(a.state) ? COLORS.wheat : COLORS.label;
        ctx.textBaseline = 'top';
        ctx.fillText(status, c.x + 10, c.y + 5);
        ctx.restore();
      }
    }
  }

  // Where a selected aircraft would fly if sent to the hovered field.
  function drawOpsPreview(preview) {
    if (!preview?.path) return;
    strokePolyline(preview.path.map(px), { color: preview.ok ? COLORS.ghostStrong : COLORS.alert, width: 1.6, dash: [3, 4] });
    if (preview.crossing) drawConflictMark(preview.crossing.at, 0);
  }

  // The instructor's pointer: a slow pulse around what to look at next.
  function drawCoachMark(at, elapsed) {
    const c = px(at);
    const r = 15 + Math.sin(elapsed * 4) * 3;
    ctx.save();
    ctx.beginPath();
    ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
    ctx.strokeStyle = COLORS.predict;
    ctx.lineWidth = 2;
    ctx.shadowColor = COLORS.predict;
    ctx.shadowBlur = 10;
    ctx.stroke();
    ctx.restore();
  }

  // ---- reroute previews --------------------------------------------------

  // Hollow diamond marking a route's (previewed) hub end.
  function drawHubEndMarker(at, color) {
    const c = px(at);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(c.x, c.y - 7); ctx.lineTo(c.x + 7, c.y); ctx.lineTo(c.x, c.y + 7); ctx.lineTo(c.x - 7, c.y);
    ctx.closePath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.restore();
  }

  // The route a Reroute would fly, with the conflicts it would still have.
  function drawGhostRoute(pv, strong) {
    const a = px(pv.from);
    const b = px(pv.to);
    const color = strong ? COLORS.ghostStrong : COLORS.ghost;
    ctx.save();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = color;
    ctx.lineWidth = strong ? 1.8 : 1.2;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.restore();
    if (!strong) return;
    drawHubEndMarker(pv.hubEndAt, color);
    for (const c of pv.conflicts) drawConflictMark(c.at, 0);
  }

  // While dragging a route away from any hub: a rubber band from its fixed end.
  function drawRubberBand(fixed, pointer) {
    const a = px(fixed);
    ctx.save();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = COLORS.ghost;
    ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(pointer.x, pointer.y); ctx.stroke();
    ctx.restore();
  }

  // Ring around the hub a dragged route would be dropped on.
  function drawDropTarget(hub, commit) {
    const c = px(hub);
    ctx.save();
    ctx.beginPath();
    ctx.arc(c.x, c.y, minDim() * 0.028 + 7, 0, Math.PI * 2);
    ctx.strokeStyle = commit ? COLORS.ghostStrong : COLORS.ghost;
    ctx.lineWidth = commit ? 2 : 1.2;
    ctx.setLineDash(commit ? [] : [3, 3]);
    ctx.stroke();
    ctx.restore();
  }

  // Plans leaving from the same point (usually a hub) stack their labels
  // instead of printing over each other.
  function labelStacks(plans) {
    const seen = new Map();
    return plans.map((p) => {
      const key = `${Math.round(p.from.x * 200)},${Math.round(p.from.y * 200)}`;
      const n = seen.get(key) || 0;
      seen.set(key, n + 1);
      return n;
    });
  }

  /**
   * Draw a frame. `selection` is { kind: 'plan' | 'drone', id } or null.
   * `overlay` (optional) adds reroute previews:
   *   ghost: { preview, strong }  a previewAdjustment result to draw as a ghost route
   *   drag:  { fixed, pointer, hubIdx, commit }  a route being dragged (pointer in px)
   */
  function draw(s, selection, overlay = {}) {
    const isSel = (kind, id) => Boolean(selection) && selection.kind === kind && selection.id === id;
    ctx.clearRect(0, 0, w, h);
    drawGrid();
    drawFields();
    if (s.ops) drawWorkOrder(s.ops);
    if (s.ops) drawFieldHazards(s.ops);
    drawHubs();
    if (s.capacityEnabled) drawZones(s);
    if (s.alerts) drawHubAlerts(s.alerts);
    const plans = [...s.plans].sort((a, b) => a.departAt - b.departAt || a.id - b.id);
    const stacks = labelStacks(plans);
    plans.forEach((p, i) => drawFiledPlan(p, s.elapsed, isSel('plan', p.id), stacks[i]));
    const { ghost, drag } = overlay;
    if (drag && drag.hubIdx >= 0) drawDropTarget(HUBS[drag.hubIdx], drag.commit);
    if (ghost && ghost.preview) drawGhostRoute(ghost.preview, ghost.strong);
    else if (drag) drawRubberBand(drag.fixed, drag.pointer);
    for (const d of s.drones) drawDrone(d, s.elapsed, isSel('drone', d.id));
    if (s.ops) drawOpsAircraft(s.ops, selection, s.elapsed);
    if (overlay.opsPreview) drawOpsPreview(overlay.opsPreview);
    if (overlay.coach) drawCoachMark(overlay.coach, s.elapsed);
    if (s.alerts) drawFieldAlerts(s.alerts, s.elapsed);
    drawWindIndicator(s);
  }

  function toLocal(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function distToSegment(m, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((m.x - a.x) * dx + (m.y - a.y) * dy) / len2)) : 0;
    return Math.hypot(m.x - (a.x + dx * t), m.y - (a.y + dy * t));
  }

  /**
   * Everything under a click, nearest first, as { kind, id }: airborne drones,
   * then filed plans by their origin marker, then plans by their dashed line.
   * `radius` is the hit distance in CSS pixels (larger for touch).
   */
  function hitTest(clientX, clientY, s, radius = 14) {
    const m = toLocal(clientX, clientY);
    const hits = [];
    for (const a of s.ops?.aircraft ?? []) {
      const dd = Math.hypot(px(a).x - m.x, px(a).y - m.y);
      if (dd < radius) hits.push({ kind: 'ops', id: a.id, rank: 0, dd });
    }
    for (const d of s.drones) {
      const dd = Math.hypot(px(d).x - m.x, px(d).y - m.y);
      if (dd < radius) hits.push({ kind: 'drone', id: d.id, rank: 0, dd });
    }
    for (const p of s.plans) {
      const a = px(p.from);
      const dm = Math.hypot(a.x - m.x, a.y - m.y);
      if (dm < radius) { hits.push({ kind: 'plan', id: p.id, rank: 1, dd: dm }); continue; }
      const dl = distToSegment(m, a, px(p.to));
      if (dl < radius * 0.6) hits.push({ kind: 'plan', id: p.id, rank: 2, dd: dl });
    }
    hits.sort((a, b) => a.rank - b.rank || a.dd - b.dd);
    return hits.map(({ kind, id }) => ({ kind, id }));
  }

  /** Where a selection sits on the canvas, in CSS pixels, or null if it's gone. */
  function anchorOf(s, selection) {
    if (!selection) return null;
    if (selection.kind === 'ops') {
      const a = s.ops?.aircraft.find((x) => x.id === selection.id);
      return a ? px(a) : null;
    }
    if (selection.kind === 'drone') {
      const d = s.drones.find((x) => x.id === selection.id);
      return d ? px(d) : null;
    }
    const p = s.plans.find((x) => x.id === selection.id);
    return p ? px(p.from) : null;
  }

  /** The landing zone (#33) under a client point, or null. */
  function zoneAt(clientX, clientY, radius = 14) {
    const m = toLocal(clientX, clientY);
    let best = null;
    let bestD = radius;
    for (const z of ZONES) {
      const c = px(z);
      const d = Math.hypot(c.x - m.x, c.y - m.y);
      if (d < bestD) { best = z; bestD = d; }
    }
    return best;
  }

  /** The FIELDS index under a client point, or null. */
  function fieldAt(clientX, clientY) {
    const m = toLocal(clientX, clientY);
    const i = FIELDS.findIndex((f) => m.x >= f.x * w && m.x <= (f.x + f.w) * w && m.y >= f.y * h && m.y <= (f.y + f.h) * h);
    return i < 0 ? null : i;
  }

  return {
    draw,
    hitTest,
    fieldAt,
    zoneAt,
    anchorOf,
    resize,
    size: () => ({ w, h }),
    /** A scope point (0..1 fractions) in canvas CSS px. */
    toPx: px,
    /** Client coordinates in canvas CSS px. */
    toLocal,
    /** Every hub's centre in canvas CSS px, in HUBS order. */
    hubPoints: () => HUBS.map(px),
  };
}
